import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

/**
 * AddPlayersModal - Bulk add players to a team
 * @param {number} teamId - Team to add players to
 * @param {string} teamName - Team name for display
 * @param {boolean} isOpen - Modal visibility
 * @param {function} onClose - Close callback
 * @param {function} onSuccess - Success callback with created players
 */
export const AddPlayersModal = ({ teamId, teamName, isOpen, onClose, onSuccess }) => {
  const [players, setPlayers] = useState([
    { first_name: '', last_name: '', date_of_birth: '' }
  ]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  if (!isOpen) return null;

  const addPlayerSlot = () => {
    setPlayers([...players, { first_name: '', last_name: '', date_of_birth: '' }]);
  };

  const removePlayerSlot = (index) => {
    if (players.length === 1) return; // Keep at least one
    setPlayers(players.filter((_, i) => i !== index));
  };

  const updatePlayer = (index, field, value) => {
    const updated = [...players];
    updated[index][field] = value;
    setPlayers(updated);
  };

  const validatePlayers = () => {
    const newErrors = [];
    players.forEach((player, index) => {
      if (!player.first_name.trim()) {
        newErrors.push(`Player ${index + 1}: First name required`);
      }
      if (!player.last_name.trim()) {
        newErrors.push(`Player ${index + 1}: Last name required`);
      }
      if (!player.date_of_birth) {
        newErrors.push(`Player ${index + 1}: Date of birth required`);
      }
    });
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePlayers()) {
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/players/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          team_id: teamId,
          players: players
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create players');
      }

      const data = await response.json();
      onSuccess(data.players);
      handleClose();
    } catch (error) {
      console.error('Error creating players:', error);
      setErrors([error.message]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPlayers([{ first_name: '', last_name: '', date_of_birth: '' }]);
    setErrors([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold">Add Players to {teamName}</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={loading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Errors */}
          {errors.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-semibold text-red-800 mb-2">Validation Errors:</p>
              <ul className="list-disc list-inside text-red-700 text-sm">
                {errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Player Slots */}
          <div className="space-y-4">
            {players.map((player, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-semibold text-gray-700">Player {index + 1}</span>
                  {players.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePlayerSlot(index)}
                      className="text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1">First Name *</label>
                    <input
                      type="text"
                      value={player.first_name}
                      onChange={(e) => updatePlayer(index, 'first_name', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Lionel"
                      disabled={loading}
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={player.last_name}
                      onChange={(e) => updatePlayer(index, 'last_name', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Messi"
                      disabled={loading}
                      required
                    />
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      value={player.date_of_birth}
                      onChange={(e) => updatePlayer(index, 'date_of_birth', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      disabled={loading}
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Another Button */}
          <button
            type="button"
            onClick={addPlayerSlot}
            className="mt-4 w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-gray-600 hover:text-blue-600 flex items-center justify-center gap-2"
            disabled={loading}
          >
            <Plus size={20} />
            Add Another Player
          </button>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              disabled={loading}
            >
              {loading ? 'Saving...' : `Save ${players.length} Player${players.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPlayersModal;
